import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Toast, ToastContainer } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
  AGENTS_DOMAIN_KEY,
  loadApplicationCatalog,
  REPORTS_DOMAIN_KEY,
} from '../../config/feedbackCatalog';
import { API_ENDPOINTS } from '../../config/apiConfig.js';
import './FeedbackWidget.css';

const MAX_ATTACHMENT_COUNT = 3;
const MAX_ATTACHMENT_TOTAL_BYTES = 5 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'csv', 'txt', 'log', 'json'
]);
const ACCEPTED_ATTACHMENT_INPUT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.log,.json';
const PASTED_ATTACHMENT_EXTENSION_BY_TYPE = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'application/json': 'json',
};
const ATTACHMENT_HELP_TEXT = 'Attach up to 3 files: PDF, images, Office docs, CSV, TXT, LOG or JSON. Max 5 MB total. You can also paste files with Ctrl+V.';

// Template for problem reports
const PROBLEM_TEMPLATE = `Description:
[Brief description of the problem]

Steps to reproduce:
1. [First step]
2. [Second step]
3. [What happens]`;

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(fileName) {
  const parts = String(fileName || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function getClipboardFiles(event) {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return [];

  const itemFiles = Array.from(clipboardData.items || [])
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter(Boolean);
  const files = itemFiles.length ? itemFiles : Array.from(clipboardData.files || []);

  return files.map((file, index) => {
    if (getFileExtension(file.name)) return file;

    const extension = PASTED_ATTACHMENT_EXTENSION_BY_TYPE[file.type?.toLowerCase()];
    if (!extension) return file;

    const timestamp = Date.now();
    return new File([file], `pasted-attachment-${timestamp}-${index + 1}.${extension}`, {
      type: file.type || 'application/octet-stream',
      lastModified: file.lastModified || timestamp,
    });
  });
}

function validateAttachments(items) {
  if (items.length > MAX_ATTACHMENT_COUNT) {
    return `You can attach up to ${MAX_ATTACHMENT_COUNT} files.`;
  }

  const totalBytes = items.reduce((total, item) => total + item.file.size, 0);
  if (totalBytes > MAX_ATTACHMENT_TOTAL_BYTES) {
    return `Attachments must be ${formatBytes(MAX_ATTACHMENT_TOTAL_BYTES)} or less in total.`;
  }

  const invalid = items.find(item => !ACCEPTED_ATTACHMENT_EXTENSIONS.has(getFileExtension(item.file.name)));
  if (invalid) {
    return `${invalid.file.name} is not an accepted file type.`;
  }

  const empty = items.find(item => item.file.size <= 0);
  if (empty) {
    return `${empty.file.name} is empty and cannot be attached.`;
  }

  return '';
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

const FeedbackWidget = ({ open, onClose }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [feedbackDomain, setFeedbackDomain] = useState('');
  const [feedbackSubOption, setFeedbackSubOption] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [problemDescription, setProblemDescription] = useState('');
  const [problemSteps, setProblemSteps] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [domainDefinitions, setDomainDefinitions] = useState([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    let isActive = true;
    setIsCatalogLoading(true);
    setCatalogError('');
    setDomainDefinitions([]);
    setFeedbackDomain('');
    setFeedbackSubOption('');

    loadApplicationCatalog({ signal: controller.signal })
      .then((catalog) => {
        if (isActive) setDomainDefinitions(catalog);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        if (isActive) {
          setDomainDefinitions([]);
          setCatalogError(error?.message || 'Unable to load feedback options.');
        }
      })
      .finally(() => {
        if (isActive) setIsCatalogLoading(false);
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [open]);

  useEffect(() => {
    if (user?.email) setUserEmail(user.email);
  }, [user?.email]);

  // Sync problemDescription + problemSteps into feedbackText for submission
  useEffect(() => {
    if (feedbackType === 'problem') {
      setFeedbackText(`Description:\n${problemDescription}\n\nSteps to reproduce:\n${problemSteps}`);
    } else {
      setFeedbackText('');
    }
  }, [feedbackType, problemDescription, problemSteps]);

  const selectedDomain = domainDefinitions.find((domain) => domain.value === feedbackDomain);
  const subOptions = selectedDomain?.children || [];
  const selectedSubOption = subOptions.find((option) => option.value === feedbackSubOption);
  const requiresSubSelection = subOptions.length > 0;
  const selectedAreaLabel = selectedSubOption
    ? `${selectedDomain.label} - ${selectedSubOption.label}`
    : selectedDomain?.label;

  // Validate problem template is filled
  const isProblemTemplateValid = () => {
    if (feedbackType !== 'problem') return true;
    return problemDescription.trim().length > 0 && problemSteps.trim().length > 0;
  };

  const isSubmitDisabled = !feedbackText.trim()
    || isCatalogLoading
    || !!catalogError
    || !domainDefinitions.length
    || !feedbackDomain
    || (requiresSubSelection && !selectedSubOption)
    || (feedbackType === 'problem' && !isProblemTemplateValid())
    || isSubmitting
    || !!attachmentError;

  const handleDomainChange = (event) => {
    const nextDomain = event.target.value;
    setFeedbackDomain(nextDomain);
    setFeedbackSubOption('');
  };

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addAttachments = (selectedFiles) => {
    if (!selectedFiles.length) return;

    const incoming = selectedFiles.map((file, index) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${index}`,
      file,
    }));
    const nextAttachments = [...attachments, ...incoming];
    const error = validateAttachments(nextAttachments);

    if (error) {
      setAttachmentError(error);
      clearFileInput();
      return;
    }

    setAttachments(nextAttachments);
    setAttachmentError('');
    clearFileInput();
  };

  const handleAttachmentChange = (event) => {
    addAttachments(Array.from(event.target.files || []));
  };

  const handlePaste = (event) => {
    if (isSubmitting) return;

    const pastedFiles = getClipboardFiles(event);
    if (!pastedFiles.length) return;

    event.preventDefault();
    addAttachments(pastedFiles);
  };

  const removeAttachment = (id) => {
    const nextAttachments = attachments.filter(item => item.id !== id);
    setAttachments(nextAttachments);
    setAttachmentError(validateAttachments(nextAttachments));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    if (!selectedDomain || (requiresSubSelection && !selectedSubOption)) {
      setSubmitError('Choose the application area before sending feedback.');
      setIsSubmitting(false);
      return;
    }

    // Validate problem template
    if (feedbackType === 'problem' && !isProblemTemplateValid()) {
      setSubmitError('Please fill in both the Description and Steps to reproduce sections completely.');
      setIsSubmitting(false);
      return;
    }

    const attachmentValidation = validateAttachments(attachments);
    if (attachmentValidation) {
      setAttachmentError(attachmentValidation);
      setIsSubmitting(false);
      return;
    }

    try {
      const attachmentsPayload = attachments.length
        ? await Promise.all(attachments.map(async ({ file }) => ({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
            data: await readFileAsBase64(file),
          })))
        : undefined;

      // Send feedback to backend API
      const res = await fetch(`${API_ENDPOINTS.EMAIL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: feedbackType,
          email: userEmail,
          domain: selectedAreaLabel,
          domainKey: selectedDomain.value,
          subdomain: selectedSubOption?.label,
          subdomainKey: selectedSubOption?.value,
          report: feedbackDomain === REPORTS_DOMAIN_KEY ? selectedSubOption?.label : undefined,
          reportKey: feedbackDomain === REPORTS_DOMAIN_KEY ? selectedSubOption?.value : undefined,
          agent: feedbackDomain === AGENTS_DOMAIN_KEY ? selectedSubOption?.label : undefined,
          agentKey: feedbackDomain === AGENTS_DOMAIN_KEY ? selectedSubOption?.value : undefined,
          message: feedbackText,
          attachments: attachmentsPayload,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        let message = text;
        try {
          message = JSON.parse(text)?.message || text;
        } catch {
          message = text;
        }
        throw new Error(res.status >= 500 ? 'Failed to send feedback. Please try again later.' : (message || `HTTP ${res.status}`));
      }

      onClose();
      setFeedbackText('');
      setFeedbackType('feedback');
      setFeedbackDomain('');
      setFeedbackSubOption('');
      setProblemDescription('');
      setProblemSteps('');
      setAttachments([]);
      setAttachmentError('');
      clearFileInput();
      setShowToast(true);
      
    } catch (error) {
      console.error('Error sending feedback:', error);
      setSubmitError(error.message || 'Failed to send feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeConfig = {
    feedback: { icon: 'bi-chat-dots-fill', label: 'Feedback', color: 'var(--del-green)' },
    idea: { icon: 'bi-lightbulb-fill', label: 'Idea', color: 'var(--del-amber, #ED8B00)' },
    problem: { icon: 'bi-exclamation-triangle-fill', label: 'Problem', color: 'var(--del-red, #DA291C)' },
  };

  return (
    <>
      {/* Feedback Modal */}
      <Modal
        show={open}
        onHide={onClose}
        centered
        className="feedback-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-chat-heart me-2" />
            Share your Feedback
          </Modal.Title>
        </Modal.Header>
        <Modal.Body onPaste={handlePaste}>
          <Form onSubmit={handleSubmit} id="feedbackForm">
            {/* Type Selector - Card style */}
            <Form.Group className="mb-4">
              <Form.Label>Type</Form.Label>
              <div className="feedback-type-cards">
                {Object.entries(typeConfig).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    className={`feedback-type-card${feedbackType === key ? ' active' : ''}`}
                    onClick={() => setFeedbackType(key)}
                    style={{ '--type-color': cfg.color }}
                  >
                    <i className={`bi ${cfg.icon}`} />
                    <span>{cfg.label}</span>
                  </button>
                ))}
              </div>
            </Form.Group>
            {/* Email - Inline display */}
            <Form.Group className="mb-4">
              <div className="feedback-email-inline">
                <i className="bi bi-envelope-fill" />
                <span className="feedback-email-label">EMAIL</span>
                <span className="feedback-email-value">{userEmail || 'your.email@example.com'}</span>
              </div>
              {!user?.email && (
                <Form.Control
                  type="email"
                  placeholder="your.email@example.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="mt-2"
                />
              )}
            </Form.Group>

            {isCatalogLoading && (
              <div className="alert alert-info mt-3 mb-3 py-2 small" role="status">
                <span className="spinner-border spinner-border-sm me-2" />
                Loading feedback options...
              </div>
            )}
            {catalogError && (
              <div className="alert alert-danger mt-3 mb-3 py-2 small" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2" />
                {catalogError}
              </div>
            )}

            {/* Domain */}
            <Form.Group className="mb-4">
              <Form.Label>Domain</Form.Label>
              <Form.Select
                value={feedbackDomain}
                onChange={handleDomainChange}
                disabled={isSubmitting || isCatalogLoading || !!catalogError}
                required
              >
                <option value="">Select an area</option>
                {domainDefinitions.map((domain) => (
                  <option key={domain.value} value={domain.value}>{domain.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {requiresSubSelection && (
              <Form.Group className="mb-4 feedback-report-select">
                <Form.Label>{selectedDomain.childLabel || 'Sub-area'}</Form.Label>
                <Form.Select
                  value={feedbackSubOption}
                  onChange={(e) => setFeedbackSubOption(e.target.value)}
                  disabled={isSubmitting || isCatalogLoading || !!catalogError}
                  required
                >
                  <option value="">Select a {selectedDomain.childLabel?.toLowerCase() || 'sub-area'}</option>
                  {subOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {feedbackType === 'problem' ? (
              <>
                <Form.Group className="mb-4">
                  <Form.Label className="feedback-problem-label">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Brief description of the problem..."
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label className="feedback-problem-label">Steps to reproduce</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder={"1. First step\n2. Second step\n3. What happens"}
                    value={problemSteps}
                    onChange={(e) => setProblemSteps(e.target.value)}
                    required
                  />
                </Form.Group>
              </>
            ) : (
              <Form.Group className="mb-4">
                <Form.Label>
                  {feedbackType === 'feedback' ? 'Your Feedback' : 'Your Idea'}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder={
                    feedbackType === 'feedback'
                      ? 'Share your experience, improvement suggestions or report problems...'
                      : 'Share your idea to improve the system...'
                  }
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  required
                />
              </Form.Group>
            )}
            <Form.Group className="mt-3 mb-0">
              <Form.Label>Attachments (optional)</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_ATTACHMENT_INPUT}
                onChange={handleAttachmentChange}
                disabled={isSubmitting}
              />
              <Form.Text className={`feedback-attachment-help${attachmentError ? ' is-error' : ''}`}>
                {ATTACHMENT_HELP_TEXT}
              </Form.Text>
              {attachmentError && (
                <div className="feedback-attachment-error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  <span>{attachmentError}</span>
                </div>
              )}
              {attachments.length > 0 && (
                <div className="feedback-attachment-list" aria-label="Selected attachments">
                  {attachments.map(({ id, file }) => (
                    <div className="feedback-attachment-item" key={id}>
                      <div className="feedback-attachment-name">
                        <i className="bi bi-paperclip" />
                        <span>{file.name}</span>
                      </div>
                      <div className="feedback-attachment-meta">
                        <span>{formatBytes(file.size)}</span>
                        <button
                          type="button"
                          className="feedback-attachment-remove"
                          onClick={() => removeAttachment(id)}
                          disabled={isSubmitting}
                          aria-label={`Remove ${file.name}`}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          </Form>
          {submitError && (
            <div className="alert alert-danger mt-3 mb-0 py-2 small">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              {submitError}
            </div>
          )}
        </Modal.Body>
        <div className="custom-modal-footer">
          <div className="feedback-footer-hint">
            <i className="bi bi-shield-check" />
            <span>Your feedback helps us improve</span>
          </div>
          <button
            type="submit"
            form="feedbackForm"
            className="btn btn-primary send-btn"
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sending...
              </>
            ) : (
              <>
                Send
                <i className="bi bi-send-fill ms-2" />
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* Success Toast */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={4000} 
          autohide
          className="feedback-success-toast"
        >
          <Toast.Body>
            <div className="feedback-toast-content">
              <div className="feedback-toast-icon">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <strong>Feedback Sent!</strong>
                <p>Thank you! Your message was submitted successfully.</p>
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default FeedbackWidget;